import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  defer,
  Navigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import AppLayout from "../layouts/AppLayout";
import ErrorView from "../views/ErrorView";
import { auth } from "../config/firebaseConfig";
/* import ProfileView from "../views/ProfileView";
import DashboardView from "../views/DashboardView";
import ExploreView from "../views/ExploreView";
import AdsView from "../views/AdsView";
import SignInView from "../views/SignInView";
import SignUpView from "../views/SignUpView";
import DebugView from "../views/DebugView";
import MatchesView from "../views/MatchesView"; */
import { Suspense, lazy, useEffect } from "react";
import { Loader2 } from "lucide-react";
import ForgotPasswordView from "../views/ForgotPasswordView";
import OnboardingView from "../views/OnboardingView";
import { useProfile } from "../context/ProfileContext";
import { useTeamScope } from "../context/TeamScopeContext";
import { useProductScope } from "@/context/ProductScopeContext";
import {
  PRODUCT_LANDING_PATH,
  getStoredActiveProduct,
} from "@/lib/product-scope";
import { isPathAllowedForReportOnly } from "@/lib/report-only";
import { useSidebarNavFeature } from "@/hooks/use-sidebar-nav-features";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRemoteConfigBoolean } from "@/context/RemoteConfigContext";
import {
  hasProductAccessWithFallback,
  isAccountExpired,
} from "@/utils/productAccess";
import ProductFirstRunGate from "@/components/first-run/ProductFirstRunGate";
import InactiveView from "@/views/InactiveView";
import SettingsLayout from "../layouts/SettingsLayout";
import CoachSectionLayout from "../layouts/CoachSectionLayout";

const routeSuspenseFallback = (
  <div className="flex justify-center py-12">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

// Lazily loaded components
const TodayView = lazy(() => import("../views/TodayView"));
const CandidatesView = lazy(() => import("../views/CandidatesView"));
const MoreView = lazy(() => import("../views/MoreView"));
const AdsView = lazy(() => import("../views/AdsView"));
const ProfileView = lazy(() => import("../views/ProfileView"));
const ReportsView = lazy(() => import("../views/reports/ReportsView"));
const DebugView = lazy(() => import("../views/DebugView"));
const SignInView = lazy(() => import("../views/SignInView"));
const ImpersonateView = lazy(() => import("../views/ImpersonateView"));
const SettingsCompanyView = lazy(
  () => import("../views/settings/SettingsCompanyView"),
);
const SettingsBillingView = lazy(
  () => import("../views/settings/SettingsBillingView"),
);
const SettingsMembersView = lazy(
  () => import("../views/settings/SettingsMembersView"),
);
const SettingsNotificationsView = lazy(
  () => import("../views/settings/SettingsNotificationsView"),
);
const SettingsProfileView = lazy(
  () => import("../views/settings/SettingsProfileView"),
);
const SettingsRolesView = lazy(
  () => import("../views/settings/SettingsRolesView"),
);
const SettingsIntelligenceView = lazy(
  () => import("../views/settings/SettingsIntelligenceView"),
);
const SettingsAssistantsView = lazy(
  () => import("../views/settings/SettingsAssistantsView"),
);
const PlaybookView = lazy(() => import("../views/PlaybookView"));
const SettingsDocumentsView = lazy(
  () => import("../views/settings/SettingsDocumentsView"),
);
const AppsView = lazy(() => import("../views/AppsView"));
const CreateAccountView = lazy(() => import("../views/CreateAccountView"));
const AcceptInviteView = lazy(() => import("../views/AcceptInviteView"));
const JoinView = lazy(() => import("../views/JoinView"));
const LeadsView = lazy(() => import("../views/LeadsView"));
const LeadCreateView = lazy(() => import("../views/LeadCreateView"));
const LeadEditView = lazy(() => import("../views/LeadEditView"));
const LeadSubmittedLeadsView = lazy(
  () => import("../views/LeadSubmittedLeadsView")
);
const LeadsSettingsView = lazy(() => import("../views/LeadsSettingsView"));
const IntelligenceView = lazy(
  () => import("../views/intelligence/IntelligenceView"),
);
const TeamsIndexView = lazy(
  () => import("../views/intelligence/TeamsIndexView"),
);
const TeamDetailView = lazy(
  () => import("../views/intelligence/TeamDetailView"),
);
const IntelligenceMemberView = lazy(
  () => import("../views/intelligence/IntelligenceMemberView"),
);
const IntelligenceAssessmentTestsView = lazy(
  () => import("../views/intelligence/IntelligenceAssessmentTestsView"),
);
const IntelligencePulseView = lazy(
  () => import("../views/intelligence/IntelligencePulseView"),
);
const MobilePulseView = lazy(() => import("../views/MobilePulseView"));
const IntelligenceMemoryView = lazy(
  () => import("../views/intelligence/IntelligenceMemoryView"),
);
const IntelligenceNoEntitlementView = lazy(
  () => import("../views/intelligence/IntelligenceNoEntitlementView"),
);
const AnalyticsView = lazy(() => import("../views/AnalyticsView"));

/**
 * Teams tab home. Follows the sidebar's active-team selection — the single
 * team switcher: a specific team shows that team's roster, "All teams" (null,
 * org-wide users only) shows the browsable team index.
 */
function TeamHomeRoute() {
  const { activeTeamId } = useTeamScope();
  if (activeTeamId) {
    return <Navigate to={`/coach/teams/${activeTeamId}`} replace />;
  }
  return <TeamsIndexView />;
}

/**
 * `/` home. Dashboard/Today belongs to Coach (GRA-78); Recruit and Leads get
 * their own landing page. A returning user whose last chosen product was
 * Recruit or Leads is sent straight there instead of Today, so `/` and the
 * post-login landing follow the product they were actually using.
 */
function HomeRoute() {
  const { memberProfile } = useProfile();
  const { availableProducts } = useProductScope();
  const stored = memberProfile?.uid
    ? getStoredActiveProduct(memberProfile.uid)
    : null;
  if (stored && stored !== "coach" && availableProducts.includes(stored)) {
    return <Navigate to={PRODUCT_LANDING_PATH[stored]} replace />;
  }
  return <TodayView />;
}

/** Pulse tab that follows the sidebar's active-team selection. */
function PulseOverviewRoute() {
  const { activeTeamId, setActiveTeamId } = useTeamScope();
  const isMobile = useIsMobile();
  // `?team=` from a deep link (e.g. the "fill in these KPIs" prompt, GRA-59)
  // selects that team, like picking it in the sidebar.
  const [searchParams] = useSearchParams();
  const urlTeamId = searchParams.get("team");
  useEffect(() => {
    if (urlTeamId && urlTeamId !== activeTeamId) setActiveTeamId(urlTeamId);
  }, [urlTeamId, activeTeamId, setActiveTeamId]);
  // On phones the desktop grid becomes the one-person-per-screen rating flow (M5).
  if (isMobile) return <MobilePulseView />;
  return <IntelligencePulseView teamId={activeTeamId} embedded />;
}

/** Tests tab that follows the sidebar's active-team selection. */
function TestsOverviewRoute() {
  const { activeTeamId } = useTeamScope();
  return <IntelligenceAssessmentTestsView teamId={activeTeamId} />;
}

/** Redirects /intelligence/* old paths to new paths, preserving search params and state. */
function LegacyRedirect({ to }: { to: string }) {
  const location = useLocation();
  const params = useParams();
  const target = Object.entries(params).reduce(
    (acc, [k, v]) => acc.replace(`:${k}`, v ?? ""),
    to,
  );
  return <Navigate to={`${target}${location.search}`} replace state={location.state} />;
}

// This function returns a promise that resolves with the user data
// when the authentication state changes
const getUserData = () => {
  return new Promise((resolve, reject) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    }, reject);
  });
};

const BusinessPlanGuard = ({ children }: { children: React.ReactNode }) => {
  const { businessProfile } = useProfile();
  if (businessProfile?.accountType === "disabled") {
    return <Navigate to="/inactive" replace />;
  }

  return <>{children}</>;
};

/**
 * The only paths an expired business may still reach (GRA-45 §6).
 *
 * The dashboard explains what happened; organization and billing settings stay
 * open so a lapsed customer can see their invoice details and act on them.
 * Deliberately exact matches — `/settings/members` and the rest of the settings
 * subtree lock along with every product surface.
 */
const EXPIRED_ACCOUNT_ALLOWED_PATHS = new Set(["/", "/settings"]);

function isPathAllowedWhenExpired(pathname: string): boolean {
  // Tolerate the trailing slash React Router may hand us for "/settings/".
  const normalized =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  return (
    EXPIRED_ACCOUNT_ALLOWED_PATHS.has(normalized) ||
    normalized === "/settings/billing"
  );
}

/**
 * Sends an expired business back to the dashboard from every locked route.
 *
 * This wraps the whole authenticated shell rather than each product route, so
 * routes that are not entitlement-gated today (`/team/*`, `/analytics`,
 * `/coach`, the Intelligence settings pages) and any route added later are
 * covered by default. It runs *before* the entitlement guards so an expired
 * business lands on the dashboard instead of the "no entitlement" upsell view.
 */
const AccountActiveGuard = ({ children }: { children: React.ReactNode }) => {
  const { businessProfile, loading } = useProfile();
  const location = useLocation();
  if (
    !loading &&
    businessProfile &&
    isAccountExpired(businessProfile, Date.now()) &&
    !isPathAllowedWhenExpired(location.pathname)
  ) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

/**
 * Keeps report-only accounts on the Reports page. Wraps the whole shell, like
 * `AccountActiveGuard`, so every people-data route (Today, Coach, Pulse,
 * Teams, settings) and any route added later is closed by default. The API
 * refuses these accounts too; this only spares them dead-end pages.
 */
const ReportOnlyGuard = ({ children }: { children: React.ReactNode }) => {
  const { businessProfile, memberAccessLevel, loading } = useProfile();
  const location = useLocation();
  if (
    !loading &&
    businessProfile &&
    memberAccessLevel === "none" &&
    !isPathAllowedForReportOnly(location.pathname)
  ) {
    return <Navigate to="/reports" replace />;
  }
  return <>{children}</>;
};

/**
 * Mirrors the "candidates"/"ads" nav features' own `visible` rule
 * (config/sidebar-nav.ts): team-scoped members never see Recruit in the
 * sidebar or product switcher, so the route itself has to agree, or a team
 * member who still has the URL (bookmark, old link) lands on a page that
 * ProductScopeContext's availableProducts doesn't list as active (GRA-78).
 */
const RecruitEnabledGuard = ({ children }: { children: React.ReactNode }) => {
  const { businessProfile, memberAccessLevel, loading } = useProfile();
  if (
    !loading &&
    businessProfile &&
    (memberAccessLevel !== "full" ||
      !hasProductAccessWithFallback(businessProfile, "recruit", Date.now()))
  ) {
    return <Navigate to="/" replace />;
  }
  return <ProductFirstRunGate product="recruit">{children}</ProductFirstRunGate>;
};

/** Mirrors the "leads"/"leadsSettings" nav features' own `visible` rule — see RecruitEnabledGuard. */
const LeadsEnabledGuard = ({ children }: { children: React.ReactNode }) => {
  const { businessProfile, memberAccessLevel, loading } = useProfile();
  if (
    !loading &&
    businessProfile &&
    (memberAccessLevel !== "full" ||
      !hasProductAccessWithFallback(businessProfile, "leads", Date.now()))
  ) {
    return <Navigate to="/" replace />;
  }
  return <ProductFirstRunGate product="leads">{children}</ProductFirstRunGate>;
};

/** Redirects team-scoped members away from full-access-only surfaces. */
const FullAccessGuard = ({ children }: { children: React.ReactNode }) => {
  const { businessProfile, memberAccessLevel, loading } = useProfile();
  if (!loading && businessProfile && memberAccessLevel === "team") {
    return <Navigate to="/coach/teams" replace />;
  }
  return <>{children}</>;
};

/**
 * Mirrors the "reports" nav feature's own `visible` rule: the page is open to
 * members granted a report capability, independent of people access.
 */
const ReportsGuard = ({ children }: { children: React.ReactNode }) => {
  const { businessProfile, memberAccessLevel, loading } = useProfile();
  const feature = useSidebarNavFeature("reports");
  if (!loading && businessProfile && !feature) {
    // A report-only account without any report capability has nowhere else
    // to go (ReportOnlyGuard sends it here), so stop instead of bouncing.
    if (memberAccessLevel === "none") return <ErrorView />;
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

const IntelligenceEntitlementGuard = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { businessProfile, loading } = useProfile();
  const showIntelligence = useRemoteConfigBoolean("show_intelligence");
  if (!loading && businessProfile && !showIntelligence) {
    return <Navigate to="/" replace />;
  }
  if (
    !loading &&
    businessProfile &&
    showIntelligence &&
    !hasProductAccessWithFallback(businessProfile, "intelligence", Date.now())
  ) {
    return (
      <Suspense fallback={routeSuspenseFallback}>
        <IntelligenceNoEntitlementView />
      </Suspense>
    );
  }
  return (
    <ProductFirstRunGate product="intelligence">{children}</ProductFirstRunGate>
  );
};

export const appRouter = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route
        path="/inactive"
        element={
          <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
            <InactiveView />
          </Suspense>
        }
      />

      <Route
        element={
          <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
            <BusinessPlanGuard>
              <AccountActiveGuard>
                <ReportOnlyGuard>
                  <AppLayout />
                </ReportOnlyGuard>
              </AccountActiveGuard>
            </BusinessPlanGuard>
          </Suspense>
        }
        loader={() => defer({ userPromise: getUserData() })}
        errorElement={<ErrorView />}>
        <Route
          path="/"
          element={
            <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
              <HomeRoute />
            </Suspense>
          }
        />
        {/* Recruit lives under its own prefix (GRA-78): a page belongs to exactly
            one product, never a fallback bucket, so the active product can be
            read straight off the URL. `/recruit` itself lands on the product's
            first nav item, Candidates. */}
        <Route path="/recruit" element={<Navigate to="/recruit/candidates" replace />} />
        <Route
          path="/recruit/ads"
          element={
            <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
              <RecruitEnabledGuard>
                <AdsView />
              </RecruitEnabledGuard>
            </Suspense>
          }
        />
        <Route
          path="/recruit/ads/:id"
          element={
            <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
              <RecruitEnabledGuard>
                <AdsView />
              </RecruitEnabledGuard>
            </Suspense>
          }
        />
        {/* Candidates — merged triage workspace (1d), replacing /chat + /matches. */}
        <Route
          path="/recruit/candidates"
          element={
            <Suspense fallback={routeSuspenseFallback}>
              <RecruitEnabledGuard>
                <CandidatesView />
              </RecruitEnabledGuard>
            </Suspense>
          }
        />
        <Route
          path="/recruit/candidates/:id"
          element={
            <Suspense fallback={routeSuspenseFallback}>
              <RecruitEnabledGuard>
                <CandidatesView />
              </RecruitEnabledGuard>
            </Suspense>
          }
        />
        <Route
          path="/recruit/candidates/:id/:userId"
          element={
            <Suspense fallback={routeSuspenseFallback}>
              <RecruitEnabledGuard>
                <CandidatesView />
              </RecruitEnabledGuard>
            </Suspense>
          }
        />
        {/* Mobile "More" hub (M6) — read-only status + desktop handoff. */}
        <Route
          path="/more"
          element={
            <Suspense fallback={routeSuspenseFallback}>
              <MoreView />
            </Suspense>
          }
        />
        {/* ── Legacy paths (permanent — emails, bookmarks, stored insight CTAs) ──
            /ads and /candidates were the Recruit routes before the product got
            its own /recruit/* prefix (GRA-78); /matches and /chat predate the
            merged Candidates workspace (1d). All four funnel into /recruit/*. */}
        <Route path="/matches" element={<LegacyRedirect to="/recruit/candidates" />} />
        <Route
          path="/matches/:id"
          element={<LegacyRedirect to="/recruit/candidates/:id" />}
        />
        <Route
          path="/matches/:adId/:matchId/:candidateId"
          element={<LegacyRedirect to="/recruit/candidates/:adId/:candidateId" />}
        />
        <Route path="/chat" element={<LegacyRedirect to="/recruit/candidates" />} />
        <Route
          path="/chat/:id"
          element={<LegacyRedirect to="/recruit/candidates/:id" />}
        />
        <Route
          path="/chat/:id/:userId"
          element={<LegacyRedirect to="/recruit/candidates/:id/:userId" />}
        />
        <Route path="/ads" element={<LegacyRedirect to="/recruit/ads" />} />
        <Route
          path="/ads/:id"
          element={<LegacyRedirect to="/recruit/ads/:id" />}
        />
        <Route
          path="/candidates"
          element={<LegacyRedirect to="/recruit/candidates" />}
        />
        <Route
          path="/candidates/:id"
          element={<LegacyRedirect to="/recruit/candidates/:id" />}
        />
        <Route
          path="/candidates/:id/:userId"
          element={<LegacyRedirect to="/recruit/candidates/:id/:userId" />}
        />
        <Route
          path="/profile"
          element={
            <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
              <ProfileView />
            </Suspense>
          }
        />
        <Route path="/business" element={<Navigate to="/settings" replace />} />
        {/* Settings chrome (sub-nav) also wraps /apps, which the nav lists
            under Settings even though its URL sits at the root. */}
        <Route element={<SettingsLayout />}>
          <Route
            path="/apps"
            element={
              <Suspense fallback={routeSuspenseFallback}>
                <AppsView />
              </Suspense>
            }
          />
          <Route path="/settings">
          <Route
            index
            element={
              <Suspense fallback={routeSuspenseFallback}>
                <SettingsCompanyView />
              </Suspense>
            }
          />
          <Route
            path="billing"
            element={
              <Suspense fallback={routeSuspenseFallback}>
                <SettingsBillingView />
              </Suspense>
            }
          />
          <Route
            path="members"
            element={
              <Suspense fallback={routeSuspenseFallback}>
                <SettingsMembersView />
              </Suspense>
            }
          />
          <Route
            path="notifications"
            element={
              <Suspense fallback={routeSuspenseFallback}>
                <SettingsNotificationsView />
              </Suspense>
            }
          />
          <Route
            path="profile"
            element={
              <Suspense fallback={routeSuspenseFallback}>
                <SettingsProfileView />
              </Suspense>
            }
          />
          <Route path="roles" element={<LegacyRedirect to="/coach/teams/roles" />} />
          <Route
            path="intelligence"
            element={<LegacyRedirect to="/settings/ai" />}
          />
          <Route
            path="memory"
            element={
              <Suspense fallback={routeSuspenseFallback}>
                <IntelligenceEntitlementGuard>
                  <IntelligenceMemoryView />
                </IntelligenceEntitlementGuard>
              </Suspense>
            }
          />
          {/* Playbook moved out of Settings to its own Coach page. */}
          <Route
            path="playbook"
            element={<LegacyRedirect to="/coach/playbook" />}
          />
          <Route
            path="ai"
            element={
              <Suspense fallback={routeSuspenseFallback}>
                <IntelligenceEntitlementGuard>
                  <SettingsIntelligenceView />
                </IntelligenceEntitlementGuard>
              </Suspense>
            }
          />
          <Route
            path="assistants"
            element={
              <Suspense fallback={routeSuspenseFallback}>
                <SettingsAssistantsView />
              </Suspense>
            }
          />
          <Route
            path="documents"
            element={
              <Suspense fallback={routeSuspenseFallback}>
                <IntelligenceEntitlementGuard>
                  <SettingsDocumentsView />
                </IntelligenceEntitlementGuard>
              </Suspense>
            }
          />
          </Route>
        </Route>
        <Route
          path="/leads"
          element={
            <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
              <LeadsEnabledGuard>
                <LeadsView />
              </LeadsEnabledGuard>
            </Suspense>
          }
        />
        <Route
          path="/leads/create"
          element={
            <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
              <LeadsEnabledGuard>
                <LeadCreateView />
              </LeadsEnabledGuard>
            </Suspense>
          }
        />
        {/* Must precede /leads/:id so "settings" isn't captured as an id. */}
        <Route
          path="/leads/settings"
          element={
            <Suspense fallback={routeSuspenseFallback}>
              <LeadsEnabledGuard>
                <LeadsSettingsView />
              </LeadsEnabledGuard>
            </Suspense>
          }
        />
        <Route
          path="/leads/:id/leads"
          element={
            <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
              <LeadsEnabledGuard>
                <LeadSubmittedLeadsView />
              </LeadsEnabledGuard>
            </Suspense>
          }
        />
        <Route
          path="/leads/:id"
          element={
            <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
              <LeadsEnabledGuard>
                <LeadEditView />
              </LeadsEnabledGuard>
            </Suspense>
          }
        />
        <Route path="/integrations" element={<LegacyRedirect to="/apps" />} />
        <Route
          path="/reports"
          element={
            <Suspense fallback={routeSuspenseFallback}>
              <ReportsGuard>
                <ReportsView />
              </ReportsGuard>
            </Suspense>
          }
        />
        {/* ── Coach: the chat is the front door; every Intelligence page lives under it ── */}
        <Route
          path="/coach"
          element={
            <Suspense fallback={routeSuspenseFallback}>
              <IntelligenceEntitlementGuard>
                <IntelligenceView />
              </IntelligenceEntitlementGuard>
            </Suspense>
          }
        />
        <Route
          path="/coach/members/:memberId"
          element={
            <Suspense fallback={routeSuspenseFallback}>
              <IntelligenceEntitlementGuard>
                <IntelligenceMemberView />
              </IntelligenceEntitlementGuard>
            </Suspense>
          }
        />
        {/* The guard wraps the tabbed layout once, so the first-run wizard and
            the no-entitlement card render without tabs. Static tabs precede
            the dynamic :teamId. */}
        <Route
          path="/coach/teams"
          element={
            <IntelligenceEntitlementGuard>
              <CoachSectionLayout featureId="teams" />
            </IntelligenceEntitlementGuard>
          }
        >
          <Route index element={<TeamHomeRoute />} />
          <Route path="tests" element={<TestsOverviewRoute />} />
          <Route
            path="roles"
            element={
              <FullAccessGuard>
                <SettingsRolesView />
              </FullAccessGuard>
            }
          />
          <Route path=":teamId" element={<TeamDetailView />} />
        </Route>
        <Route
          path="/coach/performance"
          element={
            <IntelligenceEntitlementGuard>
              <CoachSectionLayout featureId="performance" />
            </IntelligenceEntitlementGuard>
          }
        >
          <Route index element={<AnalyticsView />} />
          <Route path="pulse" element={<PulseOverviewRoute />} />
        </Route>
        <Route
          path="/coach/playbook"
          element={
            <Suspense fallback={routeSuspenseFallback}>
              <IntelligenceEntitlementGuard>
                <PlaybookView />
              </IntelligenceEntitlementGuard>
            </Suspense>
          }
        />

        {/* ── Legacy paths (permanent — emails, bookmarks, stored insight CTAs) ── */}
        <Route path="/analytics" element={<LegacyRedirect to="/coach/performance" />} />
        <Route path="/team" element={<LegacyRedirect to="/coach/teams" />} />
        <Route path="/team/members/:memberId" element={<LegacyRedirect to="/coach/members/:memberId" />} />
        <Route path="/team/pulse" element={<LegacyRedirect to="/coach/performance/pulse" />} />
        <Route path="/team/tests" element={<LegacyRedirect to="/coach/teams/tests" />} />
        <Route path="/team/roles" element={<LegacyRedirect to="/coach/teams/roles" />} />
        <Route path="/team/playbook" element={<LegacyRedirect to="/coach/playbook" />} />
        <Route path="/team/:teamId" element={<LegacyRedirect to="/coach/teams/:teamId" />} />
        <Route path="/team/:teamId/members" element={<LegacyRedirect to="/coach/teams/:teamId" />} />
        <Route path="/team/*" element={<LegacyRedirect to="/coach/teams" />} />
        <Route path="/intelligence" element={<LegacyRedirect to="/coach" />} />
        <Route path="/intelligence/teams" element={<LegacyRedirect to="/coach/teams" />} />
        <Route path="/intelligence/members/:memberId" element={<LegacyRedirect to="/coach/members/:memberId" />} />
        <Route path="/intelligence/pulse-check" element={<LegacyRedirect to="/coach/performance/pulse" />} />
        <Route path="/intelligence/pulse" element={<LegacyRedirect to="/coach/performance/pulse" />} />
        <Route path="/intelligence/assessment-tests" element={<LegacyRedirect to="/coach/teams/tests" />} />
        <Route path="/intelligence/memory" element={<LegacyRedirect to="/settings/memory" />} />
        <Route path="/intelligence/settings" element={<LegacyRedirect to="/settings/ai" />} />
        <Route path="/intelligence/*" element={<LegacyRedirect to="/coach" />} />
        <Route
          path="/debug"
          element={
            <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
              <DebugView />
            </Suspense>
          }
        />
        <Route path="*" element={<ErrorView />} />
      </Route>

      <Route
        path="/sign-in"
        element={
          <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
            <SignInView />
          </Suspense>
        }
      />
      <Route
        path="/impersonate"
        element={
          <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
            <ImpersonateView />
          </Suspense>
        }
      />
      <Route
        path="/onboarding"
        element={
          <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
            <OnboardingView />
          </Suspense>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
            <ForgotPasswordView />
          </Suspense>
        }
      />
      <Route
        path="/create-account"
        element={
          <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
            <CreateAccountView />
          </Suspense>
        }
      />
      <Route
        path="/invite/accept"
        element={
          <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
            <AcceptInviteView />
          </Suspense>
        }
      />
      <Route
        path="/join"
        element={
          <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
            <JoinView />
          </Suspense>
        }
      />
    </>
  )
);
